import React from "react";
import {
	Box,
	Progress,
	Text,
	HStack,
	VStack,
	Spinner,
	Alert,
	AlertIcon,
	Button,
	Fade,
	ScaleFade
} from "@chakra-ui/react";
import { CheckIcon, WarningIcon } from "@chakra-ui/icons";

export interface FileOperationItem {
	id: string;
	fileName: string;
	progress: number;
	status: "queued" | "processing" | "completed" | "failed";
	error?: string;
}

interface FileOperationProgressProps {
	operations: FileOperationItem[];
	operationType: "upload" | "download";
	onRetry?: (id: string) => void;
	showOverallProgress?: boolean;
}

const FileOperationProgress: React.FC<FileOperationProgressProps> = ({
	operations,
	operationType,
	onRetry,
	showOverallProgress = true
}) => {
	const totalOperations = operations.length;
	const completedOperations = operations.filter((op) => op.status === "completed").length;
	const failedOperations = operations.filter((op) => op.status === "failed").length;
	const processingOperations = operations.filter((op) => op.status === "processing").length;

	const overallProgress = totalOperations > 0 ? Math.round((completedOperations / totalOperations) * 100) : 0;

	const getStatusIcon = (status: FileOperationItem["status"]) => {
		switch (status) {
			case "completed":
				return <CheckIcon color="green.500" />;
			case "failed":
				return <WarningIcon color="red.500" />;
			case "processing":
				return <Spinner size="sm" color="blue.500" />;
			case "queued":
				return <Box w="16px" h="16px" bg="gray.300" borderRadius="full" />;
			default:
				return null;
		}
	};

	const getStatusColor = (status: FileOperationItem["status"]) => {
		switch (status) {
			case "completed":
				return "green";
			case "failed":
				return "red";
			case "processing":
				return "blue";
			case "queued":
				return "gray";
			default:
				return "gray";
		}
	};

	if (operations.length === 0) {
		return null;
	}

	return (
		<Fade in={true}>
			<Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4} shadow="sm" mb={4}>
				{showOverallProgress && totalOperations > 1 && (
					<VStack spacing={2} mb={4}>
						<HStack justify="space-between" w="full">
							<Text fontSize="sm" fontWeight="medium">
								Overall {operationType} progress
							</Text>
							<Text fontSize="sm" color="gray.600">
								{completedOperations}/{totalOperations} files
							</Text>
						</HStack>
						<Progress value={overallProgress} size="lg" colorScheme="blue" w="full" borderRadius="md" bg="gray.100" />
						{processingOperations > 0 && (
							<Text fontSize="xs" color="gray.500">
								{processingOperations} file{processingOperations > 1 ? "s" : ""} in progress...
							</Text>
						)}
					</VStack>
				)}

				<VStack spacing={3} align="stretch">
					{operations.map((operation) => (
						<ScaleFade key={operation.id} in={true}>
							<Box p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
								<HStack justify="space-between" mb={2}>
									<HStack spacing={2} flex={1} minW={0}>
										{getStatusIcon(operation.status)}
										<Text fontSize="sm" fontWeight="medium" isTruncated title={operation.fileName}>
											{operation.fileName}
										</Text>
									</HStack>
									<Text fontSize="xs" color="gray.500">
										{operation.progress}%
									</Text>
								</HStack>

								<Progress
									value={operation.progress}
									size="sm"
									colorScheme={getStatusColor(operation.status)}
									bg="gray.200"
									borderRadius="sm"
								/>

								{operation.status === "failed" && operation.error && (
									<Alert status="error" size="sm" mt={2} borderRadius="md">
										<AlertIcon />
										<Text fontSize="xs" flex={1}>
											{operation.error}
										</Text>
										{onRetry && (
											<Button
												size="xs"
												colorScheme="red"
												variant="outline"
												onClick={() => onRetry(operation.id)}
												ml={2}>
												Retry
											</Button>
										)}
									</Alert>
								)}

								{operation.status === "completed" && (
									<Text fontSize="xs" color="green.600" mt={1}>
										{operationType === "upload" ? "Uploaded" : "Downloaded"} successfully
									</Text>
								)}
							</Box>
						</ScaleFade>
					))}
				</VStack>

				{failedOperations > 0 && (
					<Alert status="warning" size="sm" mt={3} borderRadius="md">
						<AlertIcon />
						<Text fontSize="sm">
							{failedOperations} file{failedOperations > 1 ? "s" : ""} failed to {operationType}
						</Text>
					</Alert>
				)}
			</Box>
		</Fade>
	);
};

export default FileOperationProgress;
